/**
 * Potwierdzenie ogłoszonej zmiany cennika przy zmianie żywej subskrypcji
 * (migracja 875, Regulamin §11 ust. 6).
 *
 * Nowa subskrypcja widzi zmianę na stronie płatności Stripe. Zmiana planu albo
 * pakietu na żywej subskrypcji strony Stripe nie ma — serwer odmawia wtedy 409
 * `zmiana_cennika_do_akceptacji` z listą zmian, a panel pokazuje to okno
 * i ponawia żądanie z identyfikatorami, które klient zaakceptował. Bez
 * potwierdzenia zakup nie przechodzi: to jest mechanizm, nie grzeczność.
 */

import { AlertTriangle, X } from 'lucide-react';
import { useCallback, useRef, useState, type ReactNode } from 'react';

export interface ZmianaDoAkceptacji {
  zmiana_id: string;
  zdanie: string;
}

/** Odmowa 409 z listą zmian do potwierdzenia (kształt `OdmowaZakupuSchema`). */
export function zmianyDoAkceptacji(body: unknown): ZmianaDoAkceptacji[] | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as { error?: unknown; zmiany?: unknown };
  if (b.error !== 'zmiana_cennika_do_akceptacji' || !Array.isArray(b.zmiany)) return null;
  const zmiany = b.zmiany.filter(
    (z): z is ZmianaDoAkceptacji =>
      !!z &&
      typeof (z as ZmianaDoAkceptacji).zmiana_id === 'string' &&
      typeof (z as ZmianaDoAkceptacji).zdanie === 'string'
  );
  return zmiany.length > 0 ? zmiany : null;
}

export type PotwierdzZmiany = (zmiany: ZmianaDoAkceptacji[]) => Promise<boolean>;

/**
 * Okno potwierdzenia jako obietnica: `potwierdz(zmiany)` rozwiązuje się
 * `true` po „Akceptuję”, `false` po „Anuluj”/zamknięciu. `okno` renderuje
 * strona, która woła zakup.
 */
export function usePotwierdzenieZmianCennika(): { potwierdz: PotwierdzZmiany; okno: ReactNode } {
  const [zmiany, setZmiany] = useState<ZmianaDoAkceptacji[] | null>(null);
  const rozstrzygnij = useRef<((tak: boolean) => void) | null>(null);

  const potwierdz = useCallback<PotwierdzZmiany>(
    (lista) =>
      new Promise<boolean>((resolve) => {
        rozstrzygnij.current?.(false);
        rozstrzygnij.current = resolve;
        setZmiany(lista);
      }),
    []
  );

  const zakoncz = (tak: boolean) => {
    rozstrzygnij.current?.(tak);
    rozstrzygnij.current = null;
    setZmiany(null);
  };

  const okno = zmiany ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-aurora-scrim backdrop-blur-[3px]">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="zmiana-cennika-tytul"
        className="bg-aurora-surface border border-aurora-border rounded-ids-xl shadow-elev-4 max-w-md w-full mx-4"
      >
        <div className="modal__head">
          <h2 id="zmiana-cennika-tytul" className="modal__title flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            Ogłoszona zmiana cennika
          </h2>
          <button
            type="button"
            onClick={() => zakoncz(false)}
            className="iconbtn ml-auto"
            aria-label="Zamknij"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="modal__body space-y-3 text-sm">
          <p className="text-aurora-text">Dla wybranej pozycji ogłoszono zmianę ceny:</p>
          <ul className="list-disc pl-5 space-y-1">
            {zmiany.map((z) => (
              <li key={z.zmiana_id}>{z.zdanie}</li>
            ))}
          </ul>
          <p className="text-aurora-muted">
            Zamawiając, akceptujesz nową cenę od wskazanego dnia (Regulamin §11 ust. 6).
            Potwierdzenie przyjdzie mailem.
          </p>
        </div>
        <div className="modal__foot flex justify-end gap-2">
          <button type="button" className="btn btn--secondary" onClick={() => zakoncz(false)}>
            Anuluj
          </button>
          <button type="button" className="btn btn--primary" onClick={() => zakoncz(true)}>
            Akceptuję i zamawiam
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { potwierdz, okno };
}
